import { JSX, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import Button from "@/components/buttons/Button.tsx";
import Spinner from "@/components/helpers/Spinner/Spinner";
import Input from "@/components/inputs/Input.tsx";
import { MODAL_ROUTES } from "@/components/modals/router/ModalRoutes";

import { useResetOtp } from "@/hooks/api/login.hooks";
import { useChangePassword, useGetUser } from "@/hooks/api/user.hooks";
import useModalRouter from "@/hooks/useModalRouter";

import { ForgotPasswordForm } from "./ForgotPasswordModal";

const ChangePassword = (): JSX.Element => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<ForgotPasswordForm>();

  const { mutate: mutatePassword } = useChangePassword();
  const { mutate: mutateSendCode } = useResetOtp();
  const { data: user } = useGetUser();
  const { closeModal, openModal } = useModalRouter();

  const [isLoading, setLoading] = useState(false);

  const onSubmit = async (data: ForgotPasswordForm) => {
    const formData = new FormData();

    if (data.confirm_password != data.new_password) {
      setError("confirm_password", { message: t("confirm-password-invalid") });

      return;
    }

    formData.append("password", data.current_password);
    formData.append("new_password", data.new_password);

    setLoading(true);

    mutatePassword(
      { formData },
      {
        onSuccess: () => {
          closeModal();
          toast(t("change-password-success"), { type: "success", theme: "dark" });
        },
        onSettled() {
          setLoading(false);
        },
        onError: (error: any) => {
          const message = error.response.data.message;

          if (message) {
            setError("current_password", { message });
            return;
          }

          setError("current_password", { message: t("error.something-wrong") });
        },
      },
    );
  };

  const openForgotPassword = (username: string): void => {
    mutateSendCode(username, {
      onSuccess() {
        openModal([MODAL_ROUTES.FORGOT_PASSWORD, MODAL_ROUTES.FORGOT_PASSWORD_CODE], {
          username,
          account_id: user.id.toString(),
        });
      },
      onError(error: any) {
        const message = error.response.data.message;

        toast(message ?? t("error.something-wrong"), { type: "error", theme: "dark" });
      },
    });
  };

  return (
    <div className="max-w-[32.5rem]">
      <div className={"staff-modal__header"}>
        <h2 className={"staff-modal__title"}>{t("auth.new-password")}</h2>
        <p className={"staff-modal__description mb-8 text-center"}>{t("auth.new-password-message")}</p>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className={"w-full space-y-10"}>
        <div className={"flex w-full flex-col gap-[0.938rem]"}>
          <Input
            type={"password"}
            errorMessage={errors.current_password?.message}
            color={"white"}
            placeholder={t("input-current-password")}
            name={"current_password"}
            register={register}
            validation={{
              required: true,
            }}
            errors={errors}
            autoComplete={"new-password"}
          />
          {user.username && (
            <button
              type={"button"}
              title={t("auth.forgot-password")}
              onClick={() => openForgotPassword(user.username)}
              className={"self-start leading-[1.313rem]"}
            >
              {t("auth.forgot-password")}
            </button>
          )}
        </div>
        <div className={"space-y-6"}>
          <Input
            type={"password"}
            color={"white"}
            placeholder={t("auth.create-new-password")}
            errorMessage={errors.new_password?.message}
            name={"new_password"}
            register={register}
            validation={{
              required: true,
            }}
            errors={errors}
            autoComplete={"new-password"}
          />
          <Input
            type={"password"}
            color={"white"}
            placeholder={t("auth.repeat-new-password")}
            name={"confirm_password"}
            errorMessage={errors.confirm_password?.message}
            register={register}
            validation={{
              required: true,
            }}
            errors={errors}
            autoComplete={"new-password"}
          />
        </div>
        <div className={"space-y-4"}>
          <Button
            type={"submit"}
            disabled={isLoading}
            title={t("Actions.save")}
            icon={isLoading ? <Spinner className="h-6 w-6" /> : undefined}
          >
            {t("Actions.save")}
          </Button>
          <Button type={"button"} title={t("Actions.cancel")} onClick={closeModal} color={"black"} className={"w-full"}>
            {t("Actions.cancel")}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ChangePassword;
